import React, { useState, useContext, useEffect } from 'react';
import {
    MdOutlineDashboardCustomize,
    MdPeople,
    MdAttachMoney,
    MdWorkOutline,
    MdTrendingUp,
    MdHelpOutline,
    MdSettings,
    MdCalendarToday,
    MdPayment,
    MdExpandMore,
    MdExpandLess,
    MdOutlineAssignment,
    MdHome,
    MdPerson
} from 'react-icons/md';
import { FaCalculator } from "react-icons/fa";
import { Link, useLocation, useParams } from 'react-router-dom';
import { removeAccessToken } from '../utils/api/authToken';
import { BiLogOutCircle } from 'react-icons/bi';
import { LiaFileContractSolid } from 'react-icons/lia';
import { AuthContext } from '../utils/context/AuthContext';
import { canAccess } from '../utils/canAccess';
import { motion, AnimatePresence } from 'framer-motion';

const SideMenu = () => {
    const { user } = useContext(AuthContext);
    const location = useLocation();
    const { id } = useParams();
    const [isProjectsOpen, setIsProjectsOpen] = useState(false);
    const [isSalesOpen, setIsSalesOpen] = useState(false);
    const [isReportsOpen, setIsReportsOpen] = useState(false);

    // Auto-expand the current section based on active route
    useEffect(() => {
        if (location.pathname.startsWith('/dashboard/projects')) {
            setIsProjectsOpen(true);
        }
        if (location.pathname.startsWith('/dashboard/sales')) {
            setIsSalesOpen(true);
        }
        if (location.pathname.startsWith('/dashboard/reports')) {
            setIsReportsOpen(true);
        }
    }, [location.pathname]);

    const toggleProjects = () => setIsProjectsOpen(!isProjectsOpen);
    const toggleSales = () => setIsSalesOpen(!isSalesOpen);
    const toggleReports = () => setIsReportsOpen(!isReportsOpen);

    const isActive = (paths) => paths.includes(location.pathname);

    const isSubmenuActive = (subPaths) => subPaths.some(path => location.pathname.startsWith(path));

    // Animation variants
    const menuItemVariants = {
        hover: {
            x: 4,
            transition: { duration: 0.2 }
        },
        tap: {
            scale: 0.98,
            transition: { duration: 0.1 }
        }
    };

    const submenuVariants = {
        hidden: {
            opacity: 0,
            height: 0,
            overflow: 'hidden'
        },
        visible: {
            opacity: 1,
            height: 'auto',
            transition: {
                duration: 0.3,
                ease: 'easeOut'
            }
        }
    };

    // Get initials for avatar
    const getInitials = () => {
        if (!user || !user.name) return 'U';
        return user.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    const menuGroups = [
        {
            title: "MAIN",
            items: [
                {
                    name: "Dashboard",
                    icon: <MdOutlineDashboardCustomize size={18} />,
                    path: "/dashboard",
                    permission: true,
                    active: isActive(['/dashboard', '/dashboard/audit-trail'])
                },
                {
                    name: "Customers",
                    icon: <MdPeople size={18} />,
                    path: "/dashboard/customers",
                    permission: canAccess(user, 'list_customer'),
                    active: isActive(['/dashboard/customers', `/dashboard/customers/${id}/view`])
                },
                {
                    name: "Properties",
                    icon: <MdHome size={18} />,
                    path: "/dashboard/properties",
                    permission: canAccess(user, 'view_property'),
                    active: isActive(['/dashboard/properties', '/dashboard/properties/new'])
                },
                {
                    name: "Leads",
                    icon: <MdTrendingUp size={18} />,
                    path: "/dashboard/leads",
                    permission: canAccess(user, 'list_leads'),
                    active: isActive(['/dashboard/leads', `/dashboard/leads/${id}/view`])
                }
            ]
        },
        {
            title: "BUSINESS",
            items: [
                {
                    name: "Trends",
                    icon: <MdAttachMoney size={18} />,
                    isSubmenu: true,
                    permission: canAccess(user, 'view_reports'),
                    isOpen: isSalesOpen,
                    toggle: toggleSales,
                    active: isSubmenuActive(['/dashboard/sales', '/dashboard/sales/customers', '/dashboard/sales/invoices']),
                    subItems: [
                        {
                            name: "Sales",
                            path: "/dashboard/sales",
                            active: isActive(['/dashboard/sales'])
                        },
                        {
                            name: "Customers",
                            path: "/dashboard/sales/customers",
                            active: isActive(['/dashboard/sales/customers'])
                        },
                        {
                            name: "Invoices",
                            path: "/dashboard/sales/invoices",
                            active: isActive(['/dashboard/sales/invoices'])
                        }
                    ]
                },
                {
                    name: "Projects",
                    icon: <MdWorkOutline size={18} />,
                    isSubmenu: true,
                    permission: canAccess(user, 'list_project'),
                    isOpen: isProjectsOpen,
                    toggle: toggleProjects,
                    active: isSubmenuActive(['/dashboard/projects', 'dashboard/projects/new']),
                    subItems: [
                        {
                            name: "Land",
                            path: "/dashboard/projects/land",
                            permission: canAccess(user, 'list_project'),
                            active: isActive(['/dashboard/projects/land'])
                        },
                        {
                            name: "Housing",
                            path: "/dashboard/projects/housing",
                            permission: canAccess(user, 'list_project'),
                            active: isActive(['/dashboard/projects/housing'])
                        }
                    ]
                },
                {
                    name: "Reports",
                    icon: <MdHelpOutline size={18} />,
                    isSubmenu: true,
                    permission: canAccess(user, 'view_reports'),
                    isOpen: isReportsOpen,
                    toggle: toggleReports,
                    active: isSubmenuActive(['/dashboard/reports', '/dashboard/reports/payments', '/dashboard/reports/contracts', '/dashboard/reports/plots']),
                    subItems: [
                        {
                            name: "General",
                            path: "/dashboard/reports",
                            active: isActive(['/dashboard/reports/general'])
                        },
                        {
                            name: "Payments",
                            path: "/dashboard/reports/payments",
                            active: isActive(['/dashboard/reports/payments'])
                        },
                        {
                            name: "Contracts",
                            path: "/dashboard/reports/contracts",
                            active: isActive(['/dashboard/reports/contracts'])
                        },
                        {
                            name: "Plots",
                            path: "/dashboard/reports/plots",
                            active: isActive(['/dashboard/reports/plots'])
                        },
                        {
                            name: "Collections",
                            path: "/dashboard/reports/collections",
                            active: isActive(['/dashboard/reports/collections'])
                        },
                        {
                            name: "Account Due",
                            path: "/dashboard/reports/accounts-due",
                            active: isActive(['/dashboard/reports/accounts-due'])
                        },
                        {
                            name: "Sales Trends",
                            path: "/dashboard/reports/sales-trends",
                            active: isActive(['/dashboard/reports/sales-trends'])
                        },
                        {
                            name: "Projects",
                            path: "/dashboard/reports/project-collections",
                            active: isActive(['/dashboard/reports/project-collections'])
                        }
                    ]
                }
            ]
        },
        {
            title: "MANAGEMENT",
            items: [
                {
                    name: "Contracts",
                    icon: <LiaFileContractSolid size={18} />,
                    path: "/dashboard/contracts",
                    permission: canAccess(user, 'list_contract'),
                    active: isActive(['/dashboard/contracts', `/dashboard/contracts/${id}/view`])
                },
                {
                    name: "Payments",
                    icon: <MdPayment size={18} />,
                    path: "/dashboard/payments",
                    permission: canAccess(user, 'list_payments'),
                    active: isSubmenuActive(['/dashboard/payments'])
                },
                {
                    name: "HR",
                    icon: <MdPeople size={18} />,
                    path: "/dashboard/hr",
                    permission: canAccess(user, 'list_human_resource'),
                    active: isActive(['/dashboard/hr', '/dashboard/hr/employees/add-new', `/dashboard/employees/${id}/view`])
                },
                {
                    name: "Tasks",
                    icon: <MdOutlineAssignment size={18} />,
                    path: "/dashboard/tasks",
                    permission: canAccess(user, 'list_tasks'),
                    active: isActive(['/dashboard/tasks'])
                },
                {
                    name: "Tickets",
                    icon: <LiaFileContractSolid size={18} />,
                    path: "/dashboard/tickets",
                    permission: canAccess(user, 'list_tickets'),
                    active: isActive(['/dashboard/tickets'])
                },
                {
                    name: "Calendar",
                    icon: <MdCalendarToday size={18} />,
                    path: "/dashboard/calendar",
                    permission: canAccess(user, 'list_events'),
                    active: isActive(['/dashboard/calendar', '/dashboard/calendar/site-visits'])
                },
                {
                    name: "Calculator",
                    icon: <FaCalculator size={16} />,
                    path: "/dashboard/calculator",
                    permission: true,
                    active: isActive(['/dashboard/calculator'])
                }
            ]
        },
        {
            title: "SYSTEM",
            items: [
                {
                    name: "Settings",
                    icon: <MdSettings size={18} />,
                    path: "/dashboard/settings",
                    permission: canAccess(user, 'view_settings'),
                    active: isActive(['/dashboard/settings'])
                },
                {
                    name: "Log out",
                    icon: <BiLogOutCircle size={18} />,
                    onClick: removeAccessToken,
                    permission: true
                }
            ]
        }
    ];

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className='w-full h-full overflow-y-auto bg-gradient-to-b from-primary to-primary/95 backdrop-blur-sm rounded-r-xl shadow-xl'
        >
            {user && (
                <div className="pr-6 py-6 mb-4 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center text-secondary font-semibold">
                            {getInitials()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate text-sm">
                                {user.name || 'User'}
                            </p>
                            <p className="text-gray-300 text-xs truncate">
                                {user.email || ''}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className="pr-4 py-2">
                {menuGroups.map((group, groupIndex) => (
                    <div key={groupIndex} className="mb-6">
                        <div className="px-2 mb-2">
                            <p className="text-xs font-medium text-gray-400 tracking-wider">{group.title}</p>
                        </div>

                        {group.items.map((item, itemIndex) => (
                            item.permission && (
                                <div key={itemIndex}>
                                    {item.isSubmenu ? (
                                        <div className="mb-1">
                                            <motion.button
                                                whileHover={menuItemVariants.hover}
                                                whileTap={menuItemVariants.tap}
                                                onClick={item.toggle}
                                                className={`w-full flex items-center justify-between rounded-lg px-3 py-2.5 text-sm ${item.active
                                                    ? 'bg-white/10 text-white'
                                                    : 'text-gray-300 hover:bg-white/5'
                                                    }`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span className={item.active ? 'text-secondary' : 'text-gray-400'}>
                                                        {item.icon}
                                                    </span>
                                                    <span>{item.name}</span>
                                                </div>
                                                <motion.div
                                                    animate={{ rotate: item.isOpen ? 180 : 0 }}
                                                    transition={{ duration: 0.3 }}
                                                >
                                                    <MdExpandMore size={20} className="text-gray-400" />
                                                </motion.div>
                                            </motion.button>

                                            <AnimatePresence>
                                                {item.isOpen && (
                                                    <motion.div
                                                        key={`submenu-${itemIndex}`}
                                                        initial="hidden"
                                                        animate="visible"
                                                        exit="hidden"
                                                        variants={submenuVariants}
                                                        className="ml-4 pl-4 border-l border-white/10"
                                                    >
                                                        {item.subItems.map((subItem, subIndex) => (
                                                            (!subItem.permission || subItem.permission) && (
                                                                <motion.div
                                                                    key={subIndex}
                                                                    whileHover={menuItemVariants.hover}
                                                                    whileTap={menuItemVariants.tap}
                                                                    className="my-1"
                                                                >
                                                                    <Link
                                                                        to={subItem.path}
                                                                        className={`flex items-center rounded-lg px-3 py-2 text-xs ${subItem.active
                                                                            ? 'text-secondary'
                                                                            : 'text-gray-400 hover:text-gray-200'
                                                                            }`}
                                                                    >
                                                                        {subItem.name}
                                                                    </Link>
                                                                </motion.div>
                                                            )
                                                        ))}
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    ) : (
                                        <motion.div
                                            key={itemIndex}
                                            whileHover={menuItemVariants.hover}
                                            whileTap={menuItemVariants.tap}
                                            className="mb-1"
                                        >
                                            {item.onClick ? (
                                                <button
                                                    onClick={item.onClick}
                                                    className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm ${item.name === 'Log out'
                                                        ? 'text-red-400 hover:bg-red-500/10'
                                                        : 'text-gray-300 hover:bg-white/5'
                                                        }`}
                                                >
                                                    <span className={item.name === 'Log out' ? 'text-red-400' : 'text-gray-400'}>
                                                        {item.icon}
                                                    </span>
                                                    {item.name}
                                                </button>
                                            ) : (
                                                <Link
                                                    to={item.path}
                                                    className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm ${item.active
                                                        ? 'bg-white/10 text-white'
                                                        : 'text-gray-300 hover:bg-white/5'
                                                        }`}
                                                >
                                                    <span className={item.active ? 'text-secondary' : 'text-gray-400'}>
                                                        {item.icon}
                                                    </span>
                                                    {item.name}
                                                </Link>
                                            )}
                                        </motion.div>
                                    )}
                                </div>
                            )
                        ))}
                    </div>
                ))}
            </div>
        </motion.div>
    );
};

export default SideMenu;